import React from "react"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    Stack,
    Typography,
} from "@mui/material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { GuestItem } from "./GuestItem"
import { MeetingAttendee } from "../../meetings/meetingTypes"
import { makeStyles } from "tss-react/mui"
import { ChevronRight } from "@mui/icons-material"

type GuestsListProps = {
    title: string
    guests: MeetingAttendee[] | undefined
    selectedIds: number[]
    onSelectGuest: (id: number) => void
    onToggleSelectAll: () => void
    allSelected: boolean
    someSelected: boolean
    handleChange: (event: React.SyntheticEvent, expanded: boolean) => void
    expanded: boolean
}

const useStyles = makeStyles()((theme) => ({
    guestsList: {
        display: "flex",
        flexDirection: "column",
    },
    emptyText: {
        padding: ".5rem 0",
        color: theme.palette.tailwind.slate[500],
    },
    divider: {
        margin: ".5rem 0",
        borderColor: theme.palette.tailwind.slate[200],
    },
    accordion: {
        ".MuiAccordionSummary-content": {
            alignItems: "center",
        },
        ".MuiAccordionSummary-expandIconWrapper": {
            marginLeft: "1.5rem",
        },
        ".MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
            transform: "rotate(90deg)",
        },
    },
    header: {
        width: "100%",
    },
    selectAll: {
        "&.MuiFormControlLabel-root": {
            margin: 0,
        },
    },
}))

export function GuestsList({
    title,
    guests,
    selectedIds,
    onSelectGuest,
    onToggleSelectAll,
    allSelected,
    someSelected,
    handleChange,
    expanded,
}: GuestsListProps): JSX.Element {
    const { classes } = useStyles()
    const hasGuests = Boolean(guests?.length)

    return (
        <Box>
            <Accordion expanded={expanded} onChange={handleChange}>
                <AccordionSummary
                    expandIcon={<ChevronRight fontSize="small" />}
                    aria-controls="panel1bh-content"
                    id="panel1bh-header"
                    className={classes.accordion}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        className={classes.header}
                    >
                        <Typography variant="subtitle1">
                            {title} ({guests?.length || 0}):
                        </Typography>
                        {hasGuests && (
                            <LocalizedStrict
                                id="service-center-reports-dashboard-meetings-drawer-select-all"
                                attrs={{ label: true }}
                            >
                                <FormControlLabel
                                    className={classes.selectAll}
                                    onClick={(event) => event.stopPropagation()}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={allSelected}
                                            indeterminate={someSelected && !allSelected}
                                            onChange={onToggleSelectAll}
                                        />
                                    }
                                    label="Select all"
                                />
                            </LocalizedStrict>
                        )}
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Box className={classes.guestsList}>
                        {guests?.length ? (
                            guests.map((guest) => (
                                <React.Fragment key={guest.id}>
                                    <Divider className={classes.divider} />
                                    <GuestItem attendee={guest} onSelect={onSelectGuest} selectedIds={selectedIds} />
                                </React.Fragment>
                            ))
                        ) : (
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-guests-empty">
                                <Typography variant="body2" className={classes.emptyText}>
                                    No participants registered.
                                </Typography>
                            </LocalizedStrict>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    )
}
